import { useMemo } from 'react';

// Core explicit strict fields list
const strictFields = [
    'Pole Audit Date', 'Pole Address', 'Pole Coordinates', 'Pole Latitude', 'Pole Longitude',
    'Pole State', 'Pole GlobalID', 'Pole Type', 'Pole Symbology', 'Pole Structure ID',
    'Pole Alternate Structure ID', 'Pole Class', 'Pole Height', 'Pole Image Links'
];

// Source keys we already processed so we don't duplicate columns
const handledSourceKeys = [
    'Audit Date', 'Address', 'Pole Address', 'Coordinates', 'Pole Coordinates', 
    'State', 'Pole State', 'GlobalID', 'GlobalId', 'ID', 'Type', 'Symbology', 
    'Structure ID', 'Alternate Structure ID', 'Class', 'Height'
];

/**
 * Custom Hook that hooks up your exact useMemo state hooks for the Kendo properties
 */
export const usePoleExportData = (queryResults) => {
    
    const exportData = useMemo(() => {
        if (!Array.isArray(queryResults)) return [];
        let dataArray = [];

        queryResults.forEach((item) => {
            if (!item) return; // Defensive safeguard against null/undefined rows

            const pole = item.fieldDict?.Poles || {};
            const location = item.fieldDict?.Location_Details || {};
            const rawCoordinates = location['Coordinates'] || location['Pole Coordinates'] || '';

            let parsedLat = '';
            let parsedLng = '';
            if (rawCoordinates) {
                const match = String(rawCoordinates).trim().match(/^(\d+\.\d+)([-+]\d+\.\d+)$/);
                if (match) {
                    parsedLat = String(match[1]); 
                    parsedLng = String(match[2]); 
                } else {
                    const fallbackMatch = String(rawCoordinates).match(/(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)|(-?\d+\.\d+)\s+(-?\d+\.\d+)/);
                    if (fallbackMatch) {
                        parsedLat = String(fallbackMatch[1] || fallbackMatch[3] || '');
                        parsedLng = String(fallbackMatch[2] || fallbackMatch[4] || '');
                    }
                }
            }

            const forceInteger = (val) => {
                if (val === null || val === undefined || val === '') return null;
                const cleaned = String(val).replace(/[^\d-]/g, ''); 
                const parsed = parseInt(cleaned, 10);
                return isNaN(parsed) ? null : parsed;
            };

            let photoUrls = [];
            // Added optional chaining check for photosDict safety
            if (item?.photosDict && typeof item.photosDict === 'object') {
                photoUrls = Object.values(item.photosDict)
                    .map(p => p ? p['Photo Path'] : null)
                    .filter(Boolean);
            }

            const formattedRow = {
                'Pole Audit Date': pole['Audit Date'] || '', 
                'Pole Address': String(location['Address'] || location['Pole Address'] || ''),
                'Pole Coordinates': String(rawCoordinates),
                'Pole Latitude': String(parsedLat),
                'Pole Longitude': String(parsedLng),
                'Pole State': String(location['State'] || location['Pole State'] || ''),
                'Pole GlobalID': String(item.GlobalId || pole['GlobalID'] || pole['ID'] || ''),
                'Pole Type': String(pole['Type'] || ''),
                'Pole Symbology': String(pole['Symbology'] || ''),
                'Pole Structure ID': String(pole['Structure ID'] || ''),
                'Pole Alternate Structure ID': String(pole['Alternate Structure ID'] || ''),        
                'Pole Class': forceInteger(pole['Class'] || pole['Pole Class']), 
                'Pole Height': forceInteger(pole['Height'] || pole['Pole Height']),          
                'Pole Image Links': photoUrls
            };

            // Capture remaining dynamic fields from Location_Details safely
            Object.keys(location).forEach(key => {
                if (!handledSourceKeys.includes(key)) {
                    const cleanKey = `Pole ${key}`.replace(/_/g, ' ');
                    const val = location[key];
                    // Defensive validation against nested object data corruption
                    formattedRow[cleanKey] = val !== null && val !== undefined 
                        ? (typeof val === 'object' ? JSON.stringify(val) : String(val)) 
                        : '';
                }
            });

            // Capture remaining dynamic fields from Poles safely
            Object.keys(pole).forEach(key => {
                if (!handledSourceKeys.includes(key)) {
                    const cleanKey = `Pole ${key}`.replace(/_/g, ' ');
                    const val = pole[key];
                    // Defensive validation against nested object data corruption
                    formattedRow[cleanKey] = val !== null && val !== undefined 
                        ? (typeof val === 'object' ? JSON.stringify(val) : String(val)) 
                        : '';
                }
            });

            dataArray.push(formattedRow);
        });

        return dataArray;
    }, [queryResults]);

    const columns = useMemo(() => {
        const forceStringFields = [
            'Pole Address', 'Pole Coordinates', 'Pole Latitude', 'Pole Longitude', 
            'Pole State', 'Pole Type', 'Pole Symbology', 'Pole Structure ID', 
            'Pole Alternate Structure ID', 'Pole Image Links'
        ];
        const forceIntegerFields = ['Pole Class', 'Pole Height'];

        const uniqueExtraFields = new Set();
        exportData.forEach(record => {
            if (!record) return;
            Object.keys(record).forEach(key => {
                if (!strictFields.includes(key)) {
                    uniqueExtraFields.add(key);
                }
            });
        });

        const finalFieldsOrder = [...strictFields, ...Array.from(uniqueExtraFields)];

        return finalFieldsOrder.map(field => {
            let colConfig = {
                field: field,
                title: field, 
                cellOptions: {},
                headerCellOptions: { background: '#FFFFFF', color: '#000000', bold: true, textAlign: 'left', verticalAlign: 'center' },
                width: 150
            };

            if (forceStringFields.includes(field) || !strictFields.includes(field)) {
                colConfig.cellOptions.type = 'String';
                colConfig.cellOptions.format = '@';
            }
            if (forceIntegerFields.includes(field)) {
                colConfig.cellOptions.type = 'Number';
                colConfig.cellOptions.format = '0'; 
            }
            if (field === 'Pole Image Links') {
                colConfig.width = 180;
                colConfig.cellOptions.wrap = true;           
                colConfig.cellOptions.verticalAlign = 'top';  
                colConfig.cellOptions.color = '#0563C1';     
                colConfig.cellOptions.underline = true;      
            }
            return colConfig;
        });
    }, [exportData]);

    return { exportData, columns };
};

/**
 * Returns the formatted file name string safely stripping ONLY invalid filesystem characters
 */
export const getFormattedFileName = (selectedProject) => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const minStr = String(now.getMinutes()).padStart(2, '0');
    
    let projName = selectedProject && typeof selectedProject === 'string' 
        ? selectedProject 
        : "Project";

    projName = projName.replace(/[\\\/?:*"<>|]/g, "");

    if (!projName.trim()) {
        projName = "Project";
    }

    return `${projName}_QueryReport_${yyyy}${mm}${dd}_${hh}${minStr}.xlsx`;
};

/**
 * Handles manual sheet row compilation with extensive data lookups falling back to empty states cleanly
 */
export const executeExcelExport = (excelExportRef, exportData) => {
    if (!excelExportRef?.current || !Array.isArray(exportData) || exportData.length === 0) {
        alert("No data available to export.");
        return;
    }

    try {
        const workbookOptions = excelExportRef.current.workbookOptions();
        if (!workbookOptions) return;
        
        workbookOptions.sheets = [];
        const standardBorder = { color: "#D3D3D3", size: 1 };

        const uniqueExtraFields = new Set();
        exportData.forEach(record => {
            if (!record) return;
            Object.keys(record).forEach(key => {
                if (!strictFields.includes(key)) {
                    uniqueExtraFields.add(key);
                }
            });
        });
        const extraFieldsArray = Array.from(uniqueExtraFields);

        const baseColumns = [
            { title: 'Pole Audit Date', width: 150 },
            { title: 'Pole Address', width: 150 },
            { title: 'Pole Coordinates', width: 150 },
            { title: 'Pole Latitude', width: 150 },
            { title: 'Pole Longitude', width: 150 },
            { title: 'Pole State', width: 150 },
            { title: 'Pole GlobalID', width: 150 },
            { title: 'Pole Type', width: 150 },
            { title: 'Pole Symbology', width: 150 },
            { title: 'Pole Structure ID', width: 150 },
            { title: 'Pole Alternate Structure ID', width: 150 },
            { title: 'Pole Class', width: 150 },
            { title: 'Pole Height', width: 150 }
        ];

        const dynamicSheet1Columns = [
            ...baseColumns,
            ...extraFieldsArray.map(field => ({ title: field, width: 150 }))
        ];

        const sheet1Rows = [];
        
        sheet1Rows.push({
            type: "header",
            cells: dynamicSheet1Columns.map(col => ({
                value: col.title,
                background: '#FFFFFF', color: '#000000', bold: true, textAlign: 'left', verticalAlign: 'center'
            }))
        });

        exportData.forEach((record) => {
            if (!record) return; 
            
            const coreCells = [
                { value: record['Pole Audit Date'] || '', type: 'String' },
                { value: record['Pole Address'] || '', type: 'String', format: '@' },
                { value: record['Pole Coordinates'] || '', type: 'String', format: '@' },
                { value: record['Pole Latitude'] || '', type: 'String', format: '@' },
                { value: record['Pole Longitude'] || '', type: 'String', format: '@' },
                { value: record['Pole State'] || '', type: 'String', format: '@' },
                { value: record['Pole GlobalID'] || '', type: 'String', format: '@' },
                { value: record['Pole Type'] || '', type: 'String', format: '@' },
                { value: record['Pole Symbology'] || '', type: 'String', format: '@' },
                { value: record['Pole Structure ID'] || '', type: 'String', format: '@' },
                { value: record['Pole Alternate Structure ID'] || '', type: 'String', format: '@' },
                { value: record['Pole Class'] !== null && record['Pole Class'] !== undefined ? record['Pole Class'] : '', type: record['Pole Class'] !== null && record['Pole Class'] !== undefined ? 'Number' : 'String', format: '0' },
                { value: record['Pole Height'] !== null && record['Pole Height'] !== undefined ? record['Pole Height'] : '', type: record['Pole Height'] !== null && record['Pole Height'] !== undefined ? 'Number' : 'String', format: '0' }
            ];

            extraFieldsArray.forEach(field => {
                coreCells.push({
                    value: record[field] !== undefined ? record[field] : '',
                    type: 'String',
                    format: '@'
                });
            });

            sheet1Rows.push({
                type: "data",
                cells: coreCells.map(cellConfig => ({
                    ...cellConfig,
                    verticalAlign: 'center',
                    textAlign: 'left',
                    borders: { left: standardBorder, right: standardBorder, top: standardBorder, bottom: standardBorder }
                }))
            });
        });

        const sheet2Columns = [
            { title: 'Pole GlobalID', width: 250 },
            { title: 'Pole Image Links', width: 250 }
        ];
        const sheet2Rows = [];

        sheet2Rows.push({
            type: "header",
            cells: sheet2Columns.map(col => ({
                value: col.title,
                background: '#FFFFFF', color: '#000000', bold: true, textAlign: 'left', verticalAlign: 'center'
            }))
        });

        exportData.forEach((record) => {
            if (!record) return;
            const globalId = record['Pole GlobalID'] || '';
            const photoUrls = Array.isArray(record['Pole Image Links']) ? record['Pole Image Links'] : [];
            
            photoUrls.forEach((url, index) => {
                if (!url) return; 
                sheet2Rows.push({
                    type: "data",
                    cells: [
                        { value: globalId, type: 'String', verticalAlign: 'center', textAlign: 'left', borders: { left: standardBorder, right: standardBorder, top: standardBorder, bottom: standardBorder } },
                        { value: `=HYPERLINK("${url}", "View Image ${index + 1}")`, color: '#0563C1', underline: true, verticalAlign: 'center', textAlign: 'left', borders: { left: standardBorder, right: standardBorder, top: standardBorder, bottom: standardBorder } }
                    ]
                });
            });
        });

        workbookOptions.sheets.push({
            name: "Pole Details Summary",
            columns: dynamicSheet1Columns.map(col => ({ width: col.width })),
            rows: sheet1Rows,
            showGridLines: true
        });

        workbookOptions.sheets.push({
            name: "Pole Image Links",
            columns: sheet2Columns.map(col => ({ width: col.width })),
            rows: sheet2Rows,
            showGridLines: true
        });

        if (workbookOptions.sheets[1] && workbookOptions.sheets[1].rows) {
            workbookOptions.sheets[1].rows.forEach((row) => {
                if (row.type === "data" && row.cells) {
                    row.cells.forEach((cell) => {
                        if (cell && typeof cell.value === "string" && cell.value.startsWith("=")) {
                            cell.formula = cell.value.substring(1); 
                            cell.value = null;                     
                        }
                    });
                }
            });
        }

        excelExportRef.current.save(workbookOptions);

    } catch (err) {
        console.error("Spreadsheet compilation failed gracefully:", err);
        alert("An error occurred while preparing the Excel file. Please try again.");
    }
};